import { differenceInMinutes, format, isSameDay } from 'date-fns';
import type { PropsWithChildren } from 'react';

import styles from './LiveStreamStatus.module.scss';
import { useLivestreamLabelCountdown } from './use-livestream-countdown';

type Props = {
	startDate: Date | null;
	endDate: Date | null;
	hasRecordings: boolean;
	dense?: boolean;
};

export function MediaLivestreamStatus({ startDate, endDate, hasRecordings, dense = false }: Props) {
	const { currentDate, livestreamState } = useLivestreamLabelCountdown(startDate, endDate);

	if (!startDate) {
		return null;
	}

	switch (livestreamState) {
		case 'FUTURE':
			if (isSameDay(currentDate, startDate)) {
				const minutesTillLive = differenceInMinutes(startDate, currentDate, { roundingMethod: 'ceil' });

				if (minutesTillLive <= 1) {
					return (
						<LiveBadge dense={dense} className={styles.highlight}>
							Gleich <span className={styles.live}>Live</span>
						</LiveBadge>
					);
				} else if (minutesTillLive <= 10) {
					return (
						<LiveBadge dense={dense} className={styles.highlight}>
							<span className={styles.live}>Live</span> in {minutesTillLive} Minuten
						</LiveBadge>
					);
				} else {
					return (
						<LiveBadge dense={dense}>
							<span className={styles.live}>Live</span> um {format(startDate, 'HH:mm')} Uhr
						</LiveBadge>
					);
				}
			} else {
				return (
					<LiveBadge dense={dense}>
						<span className={styles.live}>Live</span> am {format(startDate, 'dd.MM')}
					</LiveBadge>
				);
			}

		case 'NOW_LIVE':
			return (
				<LiveBadge dense={dense} className={styles.highlight}>
					Jetzt <span className={styles.live}>Live</span>
				</LiveBadge>
			);

		case 'PAST':
			if (hasRecordings) {
				return (
					<LiveBadge dense={dense}>
						<span className={styles.live}>Live</span> nachschauen
					</LiveBadge>
				);
			} else {
				return (
					<LiveBadge dense={dense}>
						<span className={styles.live}>Live</span> beendet
					</LiveBadge>
				);
			}

		default:
			return null;
	}
}

function LiveBadge({ children, className = '', dense }: PropsWithChildren<{ className?: string; dense: boolean }>) {
	return <div className={`${className} ${styles.status} ${dense ? styles.dense : ''}`}>{children}</div>;
}
